import { genderOptions } from '@data'
import { usePatient } from '@hook'
import { yupResolver } from '@hookform/resolvers/yup'
import { addPatientSchema } from '@schema'
import { AddPatientFormData, PatientSAttrProps } from '@type/patient.types'
import { DateInputField, FormButton, FormInputField, InternationalPhoneField, Modal, SelectInputField } from '@ui'
import { getError, showToast } from '@util'
import { Box, Text } from 'grommet'
import { FormProvider, useForm } from 'react-hook-form'
import { useProductModules } from '../../../../modules/useProductModules'

type Props = {
  onClose: () => void
  id?: string
  defaultData?: PatientSAttrProps & {
    email?: string
  }
}

export const UpdatePatient = ({ onClose, defaultData, id }: Props) => {
  const { updatePatient } = usePatient()
  const { isModuleEnabled } = useProductModules()
  const portalEnabled = isModuleEnabled('patient_portal')
  const methods = useForm<AddPatientFormData>({
    defaultValues: { ...defaultData?.attributes, email: defaultData?.email },
    resolver: yupResolver(addPatientSchema)
  })

  const {
    handleSubmit,
    formState: { isSubmitting }
  } = methods

  const onSubmit = async (formRecObj: AddPatientFormData) => {
    const { email, ref: _ref, ...patientAttrs } = formRecObj
    const formInputVal = {
      data: {
        attributes: {
          ...patientAttrs
        },
        relationships: {
          role: {
            data: {
              id: 'patient',
              type: 'role'
            }
          },
          ...(portalEnabled && email
            ? {
                user: {
                  data: {
                    id: email,
                    type: 'user'
                  }
                }
              }
            : {})
        },
        type: 'patient'
      }
    }
    try {
      await updatePatient({ formVal: formInputVal, id: id || '' })
      showToast('Patient updated successfully')
    } catch (error: any) {
      getError(error)
    }
    onClose()
  }
  return (
    <FormProvider {...methods}>
      <Modal onClose={onClose} title="Update Patient">
        <form
          className="flex min-h-0 flex-1 flex-col w-full md:w-[750px] overflow-hidden"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="flex flex-1 flex-col gap-5 overflow-auto p-6 max-w-[750px]">
            <div className="w-full flex flex-col gap-6">
              <div className="grid md:grid-cols-3 grid-cols-1 gap-6">
                <div className="flex flex-col gap-2">
                  <FormInputField label="First Name" name="first_name" placeholder="" />
                </div>
                <div className="flex flex-col gap-2">
                  <FormInputField label="Middle Name" name="middle_name" placeholder="" />
                </div>
                <div className="flex flex-col gap-2">
                  <FormInputField label="Last Name" name="last_name" placeholder="" />
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <SelectInputField
                  label="Gender"
                  labelKey={'name'}
                  name="gender"
                  options={genderOptions}
                  placeholder="Select"
                  valueKey={{ key: 'value', reduce: true }}
                />
              </div>
              {portalEnabled ? (
                <Box pad="0">
                  <FormInputField label="Portal email (invite)" name="email" placeholder="" />
                  <Text color="dark-4" margin={{ top: 'xsmall' }} size="xsmall">
                    Patient portal login email.
                  </Text>
                </Box>
              ) : null}
              <Box pad="0">
                <DateInputField label="Date Of Birth" name="date_of_birth" />
              </Box>
              <Box pad="0">
                <InternationalPhoneField label="Phone Number" name="phone_number" />
              </Box>
            </div>
          </div>
          <FormButton disabled={isSubmitting} isLoading={isSubmitting} onClose={onClose} submit="Update Patient" />
        </form>
      </Modal>
    </FormProvider>
  )
}
